import { Controller } from '@hotwired/stimulus';
import { Editor, Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { TaskList, TaskItem } from '@tiptap/extension-list';
import { Placeholder } from '@tiptap/extensions';
import { Markdown, MarkdownManager } from '@tiptap/markdown';
import Suggestion from '@tiptap/suggestion';
import { post } from '@rails/request.js';

// Toolbar commands: what each button runs and the node or mark that shows it as pressed.
const COMMANDS = {
  bold: { run: (chain) => chain.toggleBold(), active: 'bold' },
  italic: { run: (chain) => chain.toggleItalic(), active: 'italic' },
  strike: { run: (chain) => chain.toggleStrike(), active: 'strike' },
  code: { run: (chain) => chain.toggleCode(), active: 'code' },
  heading: { run: (chain) => chain.toggleHeading({ level: 3 }), active: 'heading' },
  blockquote: { run: (chain) => chain.toggleBlockquote(), active: 'blockquote' },
  codeBlock: { run: (chain) => chain.toggleCodeBlock(), active: 'codeBlock' },
  bulletList: { run: (chain) => chain.toggleBulletList(), active: 'bulletList' },
  orderedList: { run: (chain) => chain.toggleOrderedList(), active: 'orderedList' },
  taskList: { run: (chain) => chain.toggleTaskList(), active: 'taskList' }
};

// Raw HTML typed in a card text stays text, as the server shows it (escaped): "fix the <select>" keeps its tag.
MarkdownManager.prototype.parseHTMLToken = function (token) {
  return this.htmlAsLiteralText(token.text || token.raw || '', Boolean(token.block));
};

// A bare web address stays bare in the Markdown ("https://…", not "[https://…](https://…)"). Marks are rendered
// around a placeholder; context.meta.markText is the text the link covers.
const MarkdownLink = Link.extend({
  renderMarkdown (node, helpers, context) {
    const text = helpers.renderChildren(node);
    const { href } = node.attrs;
    const bare = /^https?:\/\//.test(href) && (context?.meta?.markText ?? text) === href;
    return bare ? text : `[${text}](${href})`;
  }
});

/**
 * Markdown editor - the card description and comment fields. People see formatted text (toolbar and the usual
 * shortcuts: Ctrl/⌘+B, I, E, K…), the hidden textarea gets Markdown, so the server, the API and the MCP keep
 * plain Markdown. "@" lists the board members. Files pasted, dropped or picked are uploaded as card attachments
 * and their address goes in the text (an image is shown, any other file becomes a link). Ctrl/⌘+Enter submits.
 * Targets: input - the textarea | content - where the editor mounts | submit - button shown only with text (optional)
 *          file - hidden file input | status - upload message | list - the (hidden) <ul> of member suggestions
 * Values: uploadUrl (empty: no uploads), members (Array of { id, name }), linkPrompt, uploading, uploadFailed
 * @example
 * <form data-controller="markdown-editor" data-markdown-editor-upload-url-value="/boards/1/cards/2/attachments">
 *   <button type="button" data-action="markdown-editor#run" data-markdown-editor-command-param="bold">B</button>
 *   <textarea data-markdown-editor-target="input"></textarea>
 *   <div data-markdown-editor-target="content"></div>
 * </form>
 */
export default class extends Controller {
  static targets = ['input', 'content', 'submit', 'file', 'status', 'list'];
  static values = { uploadUrl: String, members: Array, linkPrompt: String, uploading: String, uploadFailed: String };

  connect () {
    this.editor = new Editor({
      element: this.contentTarget,
      extensions: this.extensions(),
      content: this.inputTarget.value,
      contentType: 'markdown',
      editorProps: {
        attributes: {
          id: `${this.inputTarget.id}_editor`,
          class: 'md-editor__prose card-text',
          role: 'textbox',
          'aria-multiline': 'true',
          'aria-label': this.inputTarget.labels[0]?.textContent || ''
        },
        handlePaste: (_view, event) => this.pasteFiles(event),
        handleDrop: (view, event, _slice, moved) => this.dropFiles(view, event, moved)
      },
      onUpdate: () => this.sync(),
      onTransaction: () => this.refreshToolbar()
    });
    this.inputTarget.hidden = true;
    this.sync();
  }

  disconnect () {
    this.editor?.destroy();
    this.editor = null;
  }

  // mousedown on the toolbar: a clicked button does not take the focus (and the caret) from the text.
  keepFocus (event) {
    if (event.target.closest('button')) event.preventDefault();
  }

  // Toolbar button: data-markdown-editor-command-param names a COMMANDS entry.
  run (event) {
    const command = COMMANDS[event.params.command];
    if (command) command.run(this.editor.chain().focus()).run();
  }

  link () {
    const previous = this.editor.getAttributes('link').href || '';
    const href = window.prompt(this.linkPromptValue, previous || 'https://');
    if (href === null) return;

    const chain = this.editor.chain().focus().extendMarkRange('link');
    if (href.trim() === '') chain.unsetLink().run();
    else if (this.editor.state.selection.empty && !previous) chain.insertContent({ type: 'text', text: href, marks: [{ type: 'link', attrs: { href } }] }).run();
    else chain.setLink({ href }).run();
  }

  pick () {
    this.fileTarget.click();
  }

  picked () {
    this.upload(this.fileTarget.files);
    this.fileTarget.value = '';
  }

  // Textarea gets the Markdown; an empty editor gives an empty field.
  sync () {
    this.inputTarget.value = this.editor.isEmpty ? '' : this.editor.getMarkdown().trim();
    if (this.hasSubmitTarget) this.submitTarget.hidden = this.editor.isEmpty;
  }

  refreshToolbar () {
    this.element.querySelectorAll('[data-markdown-editor-command-param]').forEach((button) => {
      const command = COMMANDS[button.dataset.markdownEditorCommandParam];
      if (command) button.setAttribute('aria-pressed', String(this.editor.isActive(command.active)));
    });
  }

  extensions () {
    const extensions = [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: false, underline: false }),
      MarkdownLink.configure({ openOnClick: false, autolink: true, defaultProtocol: 'https' }),
      Image,
      TaskList,
      TaskItem.configure({ nested: true }),
      Placeholder.configure({ placeholder: this.inputTarget.placeholder }),
      // Single line breaks are kept, as they always were in card texts.
      Markdown.configure({ markedOptions: { gfm: true, breaks: true } }),
      this.shortcuts()
    ];
    if (this.hasListTarget && this.membersValue.length > 0) extensions.push(this.mentions());
    return extensions;
  }

  shortcuts () {
    const controller = this;
    return Extension.create({
      name: 'cardShortcuts',
      addKeyboardShortcuts () {
        return {
          'Mod-k': () => { controller.link(); return true; },
          'Mod-Enter': () => { controller.element.closest('form')?.requestSubmit(); return true; }
        };
      }
    });
  }

  // "@" and some letters list the matching members; arrows move, Enter/Tab insert "@Full Name ", Escape closes.
  mentions () {
    const controller = this;
    return Extension.create({
      name: 'memberMentions',
      addProseMirrorPlugins () {
        return [Suggestion({
          editor: this.editor,
          char: '@',
          allowSpaces: true,
          items: ({ query }) => controller.membersValue.filter((member) => member.name.toLowerCase().includes(query.toLowerCase())).slice(0, 6),
          command: ({ editor, range, props }) => editor.chain().focus().insertContentAt(range, `@${props.name} `).run(),
          render: () => controller.mentionList()
        })];
      }
    });
  }

  mentionList () {
    let state = { items: [], active: 0, command: null };
    const draw = () => {
      this.listTarget.replaceChildren(...state.items.map((member, index) => {
        const item = document.createElement('li');
        item.className = 'cm-mentions__option';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', String(index === state.active));
        item.textContent = member.name;
        // mousedown (not click) so the editor keeps its caret.
        item.addEventListener('mousedown', (event) => {
          event.preventDefault();
          state.command(member);
        });
        return item;
      }));
      this.listTarget.hidden = state.items.length === 0;
    };
    const update = ({ items, command }) => {
      state = { items, active: 0, command };
      draw();
    };

    return {
      onStart: update,
      onUpdate: update,
      onExit: () => { this.listTarget.hidden = true; },
      onKeyDown: ({ event }) => {
        if (this.listTarget.hidden) return false;

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          const step = event.key === 'ArrowDown' ? 1 : -1;
          state.active = (state.active + step + state.items.length) % state.items.length;
          draw();
          return true;
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          state.command(state.items[state.active]);
          return true;
        }
        if (event.key === 'Escape') {
          this.listTarget.hidden = true;
          return true;
        }
        return false;
      }
    };
  }

  // A screenshot from the clipboard is uploaded; a paste that has text stays a text paste.
  pasteFiles (event) {
    const clipboard = event.clipboardData;
    if (!this.uploadUrlValue || !clipboard || clipboard.files.length === 0 || clipboard.getData('text/plain')) return false;

    this.upload(clipboard.files);
    return true;
  }

  dropFiles (view, event, moved) {
    const files = event.dataTransfer?.files;
    if (!this.uploadUrlValue || moved || !files || files.length === 0) return false;

    const position = view.posAtCoords({ left: event.clientX, top: event.clientY });
    this.upload(files, position?.pos);
    return true;
  }

  // Uploads the files as card attachments, then inserts them where they were dropped (or at the caret).
  async upload (files, position) {
    if (!this.uploadUrlValue || files.length === 0) return;

    const body = new FormData();
    Array.from(files).forEach((file) => body.append('card_attachment[files][]', file));
    this.report(this.uploadingValue, false);

    try {
      const response = await post(this.uploadUrlValue, { body, responseKind: 'json' });
      const json = await response.json;
      if (!response.ok) {
        this.report(json.error || this.uploadFailedValue, true);
        return;
      }

      // An image is a block: a paragraph after it keeps the caret out of it, so the next insert does not replace it.
      const content = json.attachments.flatMap((attachment) => attachment.image
        ? [{ type: 'image', attrs: { src: attachment.url, alt: attachment.name } }, { type: 'paragraph' }]
        : [{ type: 'text', text: attachment.name, marks: [{ type: 'link', attrs: { href: attachment.url } }] }, { type: 'text', text: ' ' }]);
      const chain = this.editor.chain().focus();
      (position === undefined ? chain.insertContent(content) : chain.insertContentAt(position, content)).run();
      this.report(null);
    } catch {
      this.report(this.uploadFailedValue, true);
    }
  }

  report (message, failed = false) {
    if (!this.hasStatusTarget) return;

    this.statusTarget.textContent = message || '';
    this.statusTarget.hidden = !message;
    this.statusTarget.classList.toggle('is-error', failed);
    this.element.setAttribute('aria-busy', String(Boolean(message) && !failed));
  }
}
